import React, { useState, useEffect } from 'react';
import { Button, DataTable, Toast, EventFilters, ConfirmModal } from '../../../shared/components';
import { Plus, Edit, Trash2, Eye, Map } from 'lucide-react';
import { useEventManagement, useConfirm, useRoles } from '../../../shared/hooks';
import { RoleProtectedRoute } from '../../../shared/components';
import { getEffectiveEventStatus } from '../../../shared/utils/eventStatus';
import { useNavigate } from 'react-router-dom';
import EventFormModal from './EventFormModal';
// import EventSummary from './EventSummary';

/**
 * Lista de eventos con filtros avanzados
 * Permite filtrar por título, estado y rango de fechas
 */
const EventList = () => {
  const {
    events,
    allEvents,
    loading,
    error,
    filters,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    applyEventFilters,
    applyFiltersNow,
    clearFilters,
  } = useEventManagement();

  // Hook para confirmaciones
  const { confirmState, confirm, closeConfirm } = useConfirm();

  // Hook para roles
  const { isAdmin, ROLES } = useRoles();

  // Estados de modales
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  // const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Hook para estadísticas de eventos (para funcionalidad futura)
  // const { stats, loading: statsLoading, loadEventStats } = useEventStats();
  
  // Hook para navegación
  const navigate = useNavigate();

  // Estados de notificaciones
  const [toast, setToast] = useState({
    isVisible: false,
    message: '',
    type: 'success'
  });

  // Cargar eventos al montar el componente
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Mostrar notificación
  const showToast = (message, type = 'success') => {
    setToast({
      isVisible: true,
      message,
      type
    });
  };

  // Ocultar notificación
  const hideToast = () => {
    setToast(prev => ({ ...prev, isVisible: false }));
  };

  // Manejar búsqueda y filtros
  const handleSearch = async () => {
    applyFiltersNow();
  };

  // Manejar cambio de filtros (solo actualizar estado, no aplicar)
  const handleFiltersChange = (newFilters) => {
    applyEventFilters(newFilters);
  };

  // Manejar limpieza de filtros
  const handleClearFilters = () => {
    clearFilters();
    fetchEvents();
  };

  // Manejar eliminación de evento
  const handleDeleteEvent = async (event) => {
    const confirmed = await confirm({
      title: 'Confirmar Eliminación',
      message: `¿Estás seguro de que quieres eliminar el evento "${event.title}"? Esta acción no se puede deshacer.`,
      type: 'danger',
      confirmText: 'Eliminar Evento',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        await deleteEvent(event.id);
        showToast('Evento eliminado exitosamente');
      }
    });
    
    if (!confirmed) {
      showToast('Operación cancelada', 'warning');
    }
  };

  // Manejar apertura de modal de creación
  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setIsCreateModalOpen(true);
  };

  // Manejar apertura de modal de edición
  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setIsEditModalOpen(true);
  };

  // Manejar apertura de modal de visualización
  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setIsViewModalOpen(true);
  };

  // Manejar apertura de la pantalla del plano del teatro
  const handleViewTheaterMap = (event) => {
    navigate(`/admin/events/${event.id}/details`);
  };

  // Manejar apertura de modal de resumen (funcionalidad futura)
  // const handleViewSummary = async (event) => {
  //   setSelectedEvent(event);
  //   setIsSummaryModalOpen(true);
  //   // Cargar estadísticas del evento
  //   await loadEventStats(event.id);
  // };

  // Función para formatear hora sin segundos
  const formatTime = (timeString) => {
    if (!timeString) return '';
    
    // Si ya está en formato HH:MM, devolverlo tal como está
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    
    // Si está en formato HH:MM:SS, extraer solo HH:MM
    if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeString.substring(0, 5);
    }
    
    // Para cualquier otro formato, intentar extraer HH:MM
    const timeMatch = timeString.match(/^(\d{2}:\d{2})/);
    return timeMatch ? timeMatch[1] : timeString;
  };

  // Manejar cierre de modales
  const handleCloseModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    // setIsSummaryModalOpen(false);
    setSelectedEvent(null);
  };

  // Manejar creación exitosa
  const handleCreateSuccess = () => {
    handleCloseModals();
    showToast('Evento creado exitosamente');
  };

  // Manejar actualización exitosa
  const handleUpdateSuccess = () => {
    handleCloseModals();
    showToast('Evento actualizado exitosamente');
  };

  // Configurar columnas de la tabla
  const columns = [
    {
      key: 'title',
      label: 'Título',
      render: (event) => (
        <div className="font-medium text-gray-900">
          {event.title}
        </div>
      )
    },
    {
      key: 'eventDate',
      label: 'Fecha',
      render: (event) => (
        <div className="text-gray-600">
          <div>{new Date(event.eventDate + 'T00:00:00').toLocaleDateString('es-AR')}</div>
        </div>
      )
    },
    {
      key: 'eventTime',
      label: 'Hora',
      render: (event) => (
        <div className="text-gray-600">
          {formatTime(event.eventTime)}
        </div>
      )
    },
    {
      key: 'saleStartDate',
      label: 'Inicio de Venta',
      render: (event) => (
        <div className="text-gray-600">
          {event.saleStartDate ? (
            <div className="text-sm">
              <div>{new Date(event.saleStartDate).toLocaleDateString('es-AR')}</div>
              <div className="text-xs text-gray-500">
                {new Date(event.saleStartDate).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ) : (
            <span className="text-xs text-gray-400">Sin restricción</span>
          )}
        </div>
      )
    },
    {
      key: 'venue',
      label: 'Teatro',
      render: (event) => (
        <div className="text-gray-600">
          {event.venue}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Estado',
      render: (event) => {
        const statusColors = {
          upcoming: 'bg-blue-100 text-blue-800',
          completed: 'bg-gray-100 text-gray-800',
          cancelled: 'bg-red-100 text-red-800'
        };

        const statusLabels = {
          upcoming: 'Próximo',
          completed: 'Completado',
          cancelled: 'Cancelado'
        };

        const effectiveStatus = getEffectiveEventStatus(event);

        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[effectiveStatus] || 'bg-gray-100 text-gray-800'}`}>
            {statusLabels[effectiveStatus] || effectiveStatus}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (event) => (
        <div className="flex items-center space-x-2">
          {/* Ver detalles - Disponible para todos */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewEvent(event)}
            className="text-blue-600 hover:text-blue-800"
            title="Ver detalles"
          >
            <Eye className="w-4 h-4" />
          </Button>
          {/* Ver plano del teatro - Disponible para todos */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleViewTheaterMap(event)}
            className="text-purple-600 hover:text-purple-800"
            title="Ver plano del teatro"
          >
            <Map className="w-4 h-4" />
          </Button>
          {/* Editar evento - Solo para administradores */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditEvent(event)}
              className="text-green-600 hover:text-green-800"
              title="Editar evento"
            >
              <Edit className="w-4 h-4" />
            </Button>
          )}
          {/* Eliminar evento - Solo para administradores */}
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteEvent(event)}
              className="text-red-600 hover:text-red-800"
              title="Eliminar evento"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <RoleProtectedRoute requiredRoles={[ROLES.ADMIN, ROLES.USER]}>
      <div className="space-y-6">
        {/* Header de la sección */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isAdmin ? 'Gestión de Eventos' : 'Eventos'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isAdmin ? 'Administra y configura los eventos del sistema' : 'Consulta los eventos disponibles'}
            </p>
          </div>
          {/* Botón Nuevo Evento - Solo visible para administradores */}
          {isAdmin && (
            <Button
              onClick={handleCreateEvent}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Evento
            </Button>
          )}
        </div>

        {/* Filtros */}
        <EventFilters
          filters={filters}
          events={allEvents}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          onSearch={handleSearch}
          searchPlaceholder="Buscar por título..."
          loading={loading}
        />

        {/* Tabla de eventos */}
        <div className="bg-white rounded-lg shadow">
          <DataTable
            section="Eventos"
            data={events}
            columns={columns}
            loading={loading}
            error={error}
            onRetry={fetchEvents}
            emptyMessage="No se encontraron eventos"
          />
        </div>

        {/* Modales */}
        {/* Modales de creación y edición - Solo para administradores */}
        {isAdmin && (
          <>
            <EventFormModal
              isOpen={isCreateModalOpen}
              onClose={handleCloseModals}
              onSuccess={handleCreateSuccess}
              mode="create"
              createEvent={createEvent}
              updateEvent={updateEvent}
            />

            <EventFormModal
              isOpen={isEditModalOpen}
              onClose={handleCloseModals}
              onSuccess={handleUpdateSuccess}
              mode="edit"
              event={selectedEvent}
              createEvent={createEvent}
              updateEvent={updateEvent}
            />
          </>
        )}

        {/* Modal de visualización - Disponible para todos */}
        <EventFormModal
          isOpen={isViewModalOpen}
          onClose={handleCloseModals}
          mode="view"
          event={selectedEvent}
          createEvent={createEvent}
          updateEvent={updateEvent}
        />

        {/* Modal de resumen de entradas (funcionalidad movida a EventDetails) */}
        {/* <Modal
          isOpen={isSummaryModalOpen}
          onClose={handleCloseModals}
          title={`Resumen de Entradas - ${selectedEvent?.title}`}
          size="lg"
        >
          <EventSummary
            eventId={selectedEvent?.id}
            soldTickets={stats.soldTickets}
            availableTickets={stats.availableTickets}
            reservedTickets={stats.reservedTickets}
            totalTickets={stats.totalTickets}
            loading={statsLoading}
          />
        </Modal> */}

        {/* Modal de confirmación - Solo para administradores */}
        {isAdmin && (
          <ConfirmModal
            isOpen={confirmState.isOpen}
            onClose={closeConfirm}
            onConfirm={confirmState.onConfirm}
            title={confirmState.title}
            message={confirmState.message}
            type={confirmState.type}
            confirmText={confirmState.confirmText}
            cancelText={confirmState.cancelText}
            loading={confirmState.loading}
          />
        )}

        {/* Notificaciones */}
        <Toast
          isVisible={toast.isVisible}
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      </div>
    </RoleProtectedRoute>
  );
};

export default EventList;